import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { FireflyService } from './firefly.service';
import { ToastService } from './toast.service';
import {
  CustomModel,
  CustomModelOptions,
  CustomModelResponse,
  CustomModelState
} from '../models/custom-model.model';

@Injectable({
  providedIn: 'root'
})
export class CustomModelService {

  // プライベート状態管理
  private state$ = new BehaviorSubject<CustomModelState>({
    models: [],
    isLoading: false,
    error: null,
    lastUpdated: null
  });

  // 公開プロパティ
  readonly models$ = this.state$.pipe(map(state => state.models));
  readonly loading$ = this.state$.pipe(map(state => state.isLoading));
  readonly error$ = this.state$.pipe(map(state => state.error));

  // キャッシュ設定（5分間）
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  constructor(
    private fireflyService: FireflyService,
    private toastService: ToastService
  ) { }

  /**
   * カスタムモデル一覧を取得（キャッシュ機能付き）
   */
  loadCustomModels(options?: CustomModelOptions, forceRefresh: boolean = false): Observable<CustomModel[]> {
    const currentState = this.state$.value;

    // 既にローディング中の場合は何もしない
    if (currentState.isLoading) {
      return this.models$;
    }

    // キャッシュチェック
    if (!forceRefresh && this.isCacheValid(currentState)) {
      return of(currentState.models);
    }

    // ローディング開始
    this.updateState({ isLoading: true, error: null });

    // デフォルトオプション
    const defaultOptions: CustomModelOptions = {
      sortBy: 'modifiedDate',
      limit: 50,
      publishedState: 'published',
      ...options
    };

    return this.fireflyService.getCustomModels(defaultOptions).pipe(
      map((response: CustomModelResponse) => {
        if (response && response.success && response.custom_models) {
          return response.custom_models;
        } else {
          throw new Error('カスタムモデルの取得に失敗しました');
        }
      }),
      tap((models: CustomModel[]) => {
        // 成功時の状態更新
        this.updateState({
          models,
          isLoading: false,
          error: null,
          lastUpdated: new Date()
        });
      }),
      catchError((error) => {
        // エラー時の状態更新
        this.updateState({
          isLoading: false,
          error: error.message || 'カスタムモデルの取得に失敗しました'
        });

        // エラーは呼び出し元で処理

        // エラーの場合は空の配列を返す
        return of([]);
      })
    );
  }

  /**
   * IDでカスタムモデルを取得
   */
  getModelById(id: string): CustomModel | null {
    const models = this.state$.value.models;
    return models.find(model => model.assetId === id) || null;
  }

  /**
   * カスタムモデル一覧をフィルタリング
   */
  filterModels(filterFn: (model: CustomModel) => boolean): CustomModel[] {
    return this.state$.value.models.filter(filterFn);
  }

  /**
   * スタイルモデル（trainingMode="style"）のみを取得
   */
  getStyleModels(): CustomModel[] {
    return this.state$.value.models.filter(model =>
      model.trainingMode === 'style'
    );
  }

  /**
   * スタイルモデルのみのObservable
   */
  readonly styleModels$ = this.state$.pipe(
    map(state => state.models.filter(model => model.trainingMode === 'style'))
  );

  /**
   * conceptIDをプロンプトに自動追加
   */
  addConceptIdToPrompt(prompt: string, conceptId: string): string {
    if (!conceptId || prompt.includes(conceptId)) {
      return prompt;
    }

    // conceptIdを先頭に追加
    return conceptId.trim() + ' ' + prompt.trim();
  }

  /**
   * conceptIDをプロンプトから削除
   */
  removeConceptIdFromPrompt(prompt: string, conceptId: string): string {
    if (!conceptId) {
      return prompt;
    }

    return prompt.replace(new RegExp(`\\b${conceptId}\\b\\s*`, 'gi'), '').trim();
  }

  /**
   * カスタムモデルの詳細情報を取得
   */
  getModelDisplayInfo(model: CustomModel): string {
    const name = model.displayName || model.assetName;
    const conceptInfo = model.conceptId ? ` (キーワード: ${model.conceptId})` : '';
    return name + conceptInfo;
  }

  /**
   * カスタムモデルのベースモデルからmodelVersionを取得
   */
  getModelVersion(model: CustomModel): string {
    if (!model.baseModel?.name) {
      // デフォルトはimage3_custom
      return 'image3_custom';
    }

    const baseName = model.baseModel.name.toLowerCase();
    
    // ベースモデル名からmodelVersionを判定
    if (baseName.includes('image4') || baseName.includes('firefly-4')) {
      return 'image4_custom';
    } else if (baseName.includes('image5')){
      return 'image5_custom';
    } else if (baseName.includes('image3') || baseName.includes('firefly-3')) {
      return 'image3_custom';
    }
    
    // デフォルトはimage3_custom
    return 'image3_custom';
  }

  /**
   * カスタムモデルが選択可能かどうかを判定
   */
  isModelSelectable(model: CustomModel): boolean {
    return model.publishedState === 'published' && 
           (model.trainingMode === 'Style' || model.trainingMode === 'style');
  }

  /**
   * 現在のモデル一覧を取得（同期）
   */
  getCurrentModels(): CustomModel[] {
    return this.state$.value.models;
  }

  /**
   * ローディング状態を取得（同期）
   */
  isLoading(): boolean {
    return this.state$.value.isLoading;
  }

  /**
   * エラー状態をクリア
   */
  clearError(): void {
    this.updateState({ error: null });
  }

  /**
   * キャッシュをクリア
   */
  clearCache(): void {
    this.updateState({
      models: [],
      lastUpdated: null,
      error: null
    });
  }

  /**
   * 強制的に再読み込み
   */
  forceReload(options?: CustomModelOptions): Observable<CustomModel[]> {
    return this.loadCustomModels(options, true);
  }

  // プライベートメソッド

  /**
   * 状態を更新
   */
  private updateState(partialState: Partial<CustomModelState>): void {
    const currentState = this.state$.value;
    this.state$.next({ ...currentState, ...partialState });
  }

  /**
   * キャッシュが有効かチェック
   */
  private isCacheValid(state: CustomModelState): boolean {
    if (!state.lastUpdated || state.models.length === 0) {
      return false;
    }

    const now = new Date().getTime();
    const lastUpdated = state.lastUpdated.getTime();

    return (now - lastUpdated) < this.CACHE_DURATION;
  }
}